import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import { operators as o, common as c, PipelineData } from '@pendo/aggregations';
import { getSegmentAggParams, isEveryone, isExcludingAnonymous } from '@/utils/segments';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { getActiveApplicationsMap } from '@/data/session/session-globals';

export function getGuideSegment (guideSegment: string | c.PipelineData[] | c.NestedSegment | null | undefined) {
    const uselessSegment = o.pipeline(o.sources.visitors(), o.select(['visitorId']));
    if (guideSegment === 'everyone' || isEqual(guideSegment, uselessSegment)) {
        return null;
    }

    return o.segment(guideSegment);
}

// when we are passing the global segment filter params we have an identified
// operator coming from the guide audience and the `includeAnonymous` value
// and an operator coming from a `segment` operator in the guide audience.
export function resolveIdentified (identifiedProps: Array<string>) {
    const identifiedProp = identifiedProps.find((str) => str === 'visitorId');
    const anonymousProp = identifiedProps.find((str) => str === '!visitorId');

    // if there's an `identified` filtering out identified visitors AND anonymous visitors
    // then you will be filtering out ALL visitors, so let's go ahead and zero it out
    if (identifiedProp && anonymousProp) {
        return o.limit(0);
    }

    return o.identified(identifiedProp || anonymousProp);
}

function sdkVersionsPipeline (
    appIdMetadata: string | undefined,
    withVersionData: boolean,
    guide: { appId: c.AppIdType },
) {
    if (!withVersionData) {
        return [];
    }

    const pipeline = o.pipeline(
        o.filter(`!isNil(metadata.${appIdMetadata}.lastuseragent)`),
        o.group(['visitorId'], o.groupField('ua', { first: `metadata.${appIdMetadata}.lastuseragent` })),
        { useragent: { ua: 'ua' } },
        o.filter('!isNil(ua.sdkVersion) && !isNil(ua.appVersion)'),
        o.group(['ua.sdkVersion', 'ua.appVersion'], o.groupField('ua.count', o.count(null))),
        o.evaluate('ua.appVersionOrder', '0'),
        o.merge(
            ['ua.appVersion'],
            o.mappings({ 'ua.appVersion': 'version', 'ua.appVersionOrder': 'createdAt' }),
            o.pipeline({ source: { mobileapplicationversions: { appId: guide.appId } } }),
        ),
        o.reduce({ versions: o.list('ua') }),
    );

    return [pipeline];
}

export function getVisitorsInGuideSegmentPipeline ({
    blacklist = 'ignore',
    guide,
    guideAudienceIdentified,
    guideSegmentIdentified,
    guideTargetingSegment,
    appIdMetadata,
    includeAnonymous,
    segmentId,
    segmentsNeedAccountIds,
    selectedAccount,
    useGlobalSegment,
    isJourney,
    isEmail,
    isNestedSegment,
}: {
    blacklist?: c.BlacklistTypes;
    guide: { appId: c.AppIdType };
    guideAudienceIdentified?: string;
    guideSegmentIdentified?: string | null;
    guideTargetingSegment: string | Array<PipelineData>;
    appIdMetadata: string | undefined;
    includeAnonymous?: string;
    segmentId?: string | null;
    segmentsNeedAccountIds?: boolean;
    selectedAccount?: c.AccountFilterAggInput;
    useGlobalSegment?: boolean;
    isJourney?: boolean;
    isEmail?: boolean;
    emailMetadataFields?: string[];
    isNestedSegment?: boolean;
}) {
    const shouldBulkExpandAccounts = isNestedSegment;
    const applications = getActiveApplicationsMap();
    const app = applications[String(guide.appId)];
    const showGuidesToAnon = get(app, 'applicationFlags.showGuidesToAnon', false);
    const isExcludingAnon = isExcludingAnonymous(segmentId) && !showGuidesToAnon;
    const includeGlobalSegment = useGlobalSegment && !isEveryone(segmentId) && !isExcludingAnon;

    if (includeGlobalSegment) {
        const segmentAggParams = getSegmentAggParams(segmentId);
        segmentId = segmentAggParams.segmentId;
        includeAnonymous = segmentAggParams.includeAnonymous;
        blacklist = segmentAggParams.blacklist;
    } else {
        segmentId = undefined;
        includeAnonymous = undefined;
    }

    if (guideTargetingSegment === segmentId) {
        segmentId = null;
    }

    const guideSegment = getGuideSegment(guideTargetingSegment);

    const resolveIdentParams: Array<string> = [];
    if (includeAnonymous) {
        resolveIdentParams.push(includeAnonymous);
    }
    if (guideAudienceIdentified) {
        resolveIdentParams.push(guideAudienceIdentified);
    }
    if (guideSegmentIdentified) {
        resolveIdentParams.push(guideSegmentIdentified);
    }

    return [
        o.sources.visitors({
            blacklist,
            ...(!(isEmail || isJourney) && { since: 'dateAdd(now(), -12, "months")' }),
            identified: !showGuidesToAnon,
        }),
        resolveIdentified(resolveIdentParams),
        !(isEmail || isJourney) &&
            appIdMetadata &&
            o.filter(`!isNil(metadata.${appIdMetadata}.firstvisit) && 0 != metadata.${appIdMetadata}.firstvisit`),
        segmentsNeedAccountIds &&
            o.pipeline(
                o.evaluate('accountId', 'metadata.auto.accountids'),
                o.unwind('accountId', {
                    keepEmpty: true,
                }),
            ),
        o.segment(segmentId),
        ...(shouldBulkExpandAccounts ? [o.bulkExpand('account'), o.bulkExpand('parentAccount')] : []),
        o.accountFilter(selectedAccount),
        guideSegment,
    ];
}

export default {
    name: ({ useGlobalSegment }: { useGlobalSegment: boolean }) =>
        `VisitorsInGuideSegment${useGlobalSegment ? '--segmented' : ''}`.trim(),
    productArea: PRODUCT_AREAS.GUIDES,
    subProductArea: SUB_PRODUCT_AREAS.GUIDE_METRICS,
    responseFormat: 'rows',
    build: ({
        blacklist,
        guide,
        guideAudienceIdentified,
        guideSegmentIdentified,
        guideTargetingSegment,
        includeAnonymous,
        segmentId,
        segmentsNeedAccountIds,
        selectedAccount,
        useGlobalSegment,
        withVersionData = false,
        isJourney = false,
        isEmail = false,
        emailMetadataFields,
        isNestedSegment,
    }: {
        blacklist?: c.BlacklistTypes;
        guide: { appId: c.AppIdType };
        guideAudienceIdentified?: string;
        guideSegmentIdentified?: string | null;
        guideTargetingSegment: string | Array<PipelineData>;
        includeAnonymous?: string;
        segmentId?: string | null;
        segmentsNeedAccountIds?: boolean;
        selectedAccount?: c.AccountFilterAggInput;
        useGlobalSegment?: boolean;
        withVersionData?: boolean;
        isJourney?: boolean;
        isEmail?: boolean;
        emailMetadataFields?: string[];
        isNestedSegment?: boolean
    }) => {
        const appMap = getActiveApplicationsMap();
        const app = appMap[String(guide.appId)];
        const appIdMetadata = `auto_${app?.id?.toString().replace(/-/gi, '_')}`;

        const visitorPipeline = getVisitorsInGuideSegmentPipeline({
            blacklist,
            guide,
            appIdMetadata,
            useGlobalSegment,
            guideAudienceIdentified,
            guideSegmentIdentified,
            guideTargetingSegment,
            segmentsNeedAccountIds,
            selectedAccount,
            includeAnonymous,
            segmentId,
            isJourney,
            isEmail,
            emailMetadataFields,
            isNestedSegment,
        });

        return o.pipeline(
            ...visitorPipeline,
            ...((isEmail || isJourney) && emailMetadataFields && emailMetadataFields.length > 0
                ? [
                    o.evalExpression(
                        emailMetadataFields.reduce((acc: Record<string, string>, _field, idx) => {
                            acc[`_emailValid${idx}`] = `isEmailAddress(metadata.${emailMetadataFields[idx]})`;

                            return acc;
                        }, {}),
                    ),
                ]
                : []),

            o.fork(
                o.pipeline(
                    o.reduce({
                        targeted: segmentsNeedAccountIds ? o.count('visitorId') : o.count(null),
                        ...((isEmail || isJourney)
                            ? {
                                unsubscribedVisitors: o.countIf(
                                    'visitorId',
                                    'metadata.pendo.campaignemailstatus == "unsubscribed"',
                                ),
                                ...(emailMetadataFields && emailMetadataFields.length > 0
                                    ? {
                                        // Count visitors who have at least one invalid email metadata field.
                                        // Uses OR logic (||) so if any email field is invalid, the visitor is counted.
                                        invalidEmailAddress: o.countIf(
                                            'visitorId',
                                            emailMetadataFields
                                                .map((_field, idx) => `!_emailValid${idx}`)
                                                .join(' || '),
                                        ),
                                    }
                                    : {}),
                                bouncedVisitors: o.countIf(
                                    'visitorId',
                                    'metadata.pendo.campaignemailstatus == "bounced"',
                                ),
                                spamComplaintVisitors: o.countIf(
                                    'visitorId',
                                    'metadata.pendo.campaignemailstatus == "complaint"',
                                ),
                                dormantVisitors: o.countIf(
                                    'visitorId',
                                    'metadata.auto.lastvisit > 0 && metadata.auto.lastvisit < dateAdd(startOfPeriod("daily", now()), -12, "months")',
                                ),
                                ...(emailMetadataFields && emailMetadataFields.length > 0
                                    ? {
                                        // Count visitors who have all valid email metadata fields AND either have no campaign email status
                                        // or have an empty campaign email status (meaning they are not unsubscribed or bounced etc...).
                                        deliverableVisitors: o.countIf(
                                            'visitorId',
                                            `(${emailMetadataFields
                                                .map((_field, idx) => `_emailValid${idx}`)
                                                .join(' && ')}) && (isNil(metadata.pendo.campaignemailstatus) || metadata.pendo.campaignemailstatus == "")`,
                                        ),
                                    }
                                    : {}),
                            }
                            : {}),
                        ...(isEmail || isJourney
                            ? {
                                inactiveVisitors: o.countIf(
                                    'visitorId',
                                    'metadata.auto.lastvisit == 0 && metadata.auto.createdat > dateAdd(startOfPeriod("daily", now()), -12, "months")',
                                ),
                            }
                            : {}),
                    }),
                ),
                ...sdkVersionsPipeline(appIdMetadata, withVersionData, guide),
            ),
            o.join(),
            ...((isEmail || isJourney)
                ? [
                    o.evalExpression({
                        unsubscribedVisitors: 'if(isNil(unsubscribedVisitors), 0, unsubscribedVisitors)',
                        invalidEmailAddress: 'if(isNil(invalidEmailAddress),0, invalidEmailAddress)',
                        bouncedVisitors: 'if(isNil(bouncedVisitors),0, bouncedVisitors)',
                        spamComplaintVisitors: 'if(isNil(spamComplaintVisitors),0, spamComplaintVisitors)',
                        dormantVisitors: 'if(isNil(dormantVisitors), 0, dormantVisitors)',
                        deliverableVisitors: 'if(isNil(deliverableVisitors), 0, deliverableVisitors)',
                    }),
                ]
                : []),
        );
    },
};
